/** Exact public identity for one positive signed-BIGINT database row. */
export type StockyPublicBigIntId = number | string;
export type StockySourceArchiveId = StockyPublicBigIntId;
export type StockyPublicBigIntCursor = number | string;

const MAX_SIGNED_BIGINT = 9_223_372_036_854_775_807n;
const MAX_ID_TEXT_LENGTH = 19;

export function isStockyPublicBigIntId(
  value: unknown,
): value is StockyPublicBigIntId {
  if (typeof value === "number") {
    return Number.isSafeInteger(value) && value > 0;
  }
  return (
    typeof value === "string" &&
    value.length <= MAX_ID_TEXT_LENGTH &&
    /^[1-9][0-9]*$/.test(value) &&
    BigInt(value) <= MAX_SIGNED_BIGINT
  );
}

export const isStockySourceArchiveId = isStockyPublicBigIntId;

export function canonicalStockyPublicBigIntId(value: unknown): string {
  if (!isStockyPublicBigIntId(value)) {
    throw new TypeError("invalid Stocky public BIGINT id");
  }
  return String(value);
}

export function canonicalStockyPublicBigIntCursor(value: unknown): string {
  if (typeof value === "number") {
    if (!Number.isSafeInteger(value) || value < 0) {
      throw new TypeError("invalid Stocky public BIGINT cursor");
    }
    return String(value);
  }
  if (
    typeof value !== "string" ||
    value.length > MAX_ID_TEXT_LENGTH ||
    !/^(?:0|[1-9][0-9]*)$/.test(value) ||
    BigInt(value) > MAX_SIGNED_BIGINT
  ) {
    throw new TypeError("invalid Stocky public BIGINT cursor");
  }
  return value;
}

export function requireStockySourceArchiveId(
  value: StockySourceArchiveId,
): StockySourceArchiveId {
  if (!isStockyPublicBigIntId(value)) {
    throw new TypeError("invalid Stocky source archive id");
  }
  return value;
}

export function encodeStockySourceArchiveId(
  value: StockySourceArchiveId,
): string {
  return encodeURIComponent(String(requireStockySourceArchiveId(value)));
}
